/**
 * Formats a timestamp to relative time in shorthand format for times within 24 hours
 * For times older than 24 hours, returns the original string unchanged
 * @param dateString - The date string to format
 * @returns Formatted relative time string (e.g., "1 min ago", "2 hrs ago") or original string
 */
export const formatRelativeTime = (dateString: string): string => {
  if (!dateString) return '';

  // Check if it's already a human readable format and convert to shorthand only within 24 hours
  if (typeof dateString === 'string') {
    // Handle already formatted strings like "3 hours ago", "2 minutes ago", etc.
    const humanFormatMatch = dateString.match(/(\d+)\s*(second|minute|hour)s?\s*ago/i);
    if (humanFormatMatch) {
      const [, num, unit] = humanFormatMatch;
      const number = parseInt(num);
      
      switch (unit.toLowerCase()) {
        case 'second':
          return number === 1 ? '1 sec ago' : `${number} secs ago`;
        case 'minute':
          return number === 1 ? '1 min ago' : `${number} mins ago`;
        case 'hour':
          // Only format if within 24 hours
          if (number < 24) {
            return number === 1 ? '1 hr ago' : `${number} hrs ago`;
          }
          break;
      }
    }
  }

  // Try to parse as a timestamp and calculate relative time
  const date = new Date(dateString);
  
  // Check if date parsing was successful
  if (isNaN(date.getTime())) {
    return dateString; // Return original string if parsing fails
  }

  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);

  // Only format if within 24 hours (1440 minutes)
  if (diffInMinutes < 1440) {
    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes === 1) {
      return '1 min ago';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} mins ago`;
    } else if (diffInHours === 1) {
      return '1 hr ago';
    } else {
      return `${diffInHours} hrs ago`;
    }
  }

  // For anything older than 24 hours, return the original string
  return dateString;
};

/**
 * Extracts the suburb from a full Australian address string
 * Handles various formats including comma-separated and space-separated addresses
 * @param fullAddress - The full address string
 * @returns Only the suburb part of the address, or the original string if parsing fails
 */
export const extractSuburb = (fullAddress: string): string => {
  if (!fullAddress) return '';

  const match = fullAddress.match(/([\w\s]+?)\s+(VIC|Victoria|NSW|New South Wales|Queensland|QLD|WA|SA|TAS|ACT|NT)\b/i);

  const suburb = match ? match[1].trim() : null;

  // // Clean up the address - remove extra spaces and normalize
  // const cleanAddress = fullAddress.replace(/\s+/g, ' ').trim();
  
  // // Split by commas first
  // const commaParts = cleanAddress.split(',').map(part => part.trim()).filter(part => part);
  
  // // Australian state codes and names for identification
  // const statePatterns = /\b(NSW|VIC|QLD|WA|SA|TAS|ACT|NT|New South Wales|Victoria|Queensland|Western Australia|South Australia|Tasmania|Australian Capital Territory|Northern Territory)\b/i;
  // const postcodePattern = /\b\d{4}\b/;
  
  // if (commaParts.length >= 2) {
  //   // Find the part that contains state/postcode (usually the last part)
  //   let statePartIndex = -1;
  //   for (let i = commaParts.length - 1; i >= 0; i--) {
  //     if (statePatterns.test(commaParts[i]) || postcodePattern.test(commaParts[i])) {
  //       statePartIndex = i;
  //       break;
  //     }
  //   }
    
  //   if (statePartIndex > 0) {
  //     // Return the part before the state/postcode part (which should be the suburb)
  //     return commaParts[statePartIndex - 1];
  //   } else if (commaParts.length === 2) {
  //     // If we have exactly 2 parts and couldn't identify state, assume second part is suburb
  //     return commaParts[1];
  //   } else if (commaParts.length >= 2) {
  //     // Default to second-to-last part if we have multiple parts
  //     return commaParts[commaParts.length - 2];
  //   }
  // }
  
  // // If no commas, try to parse space-separated address
  // const spaceParts = cleanAddress.split(' ').filter(part => part);
  // if (spaceParts.length >= 3) {
  //   // Look for pattern like "123 Main Street Melbourne VIC 3000"
  //   // Find state position
  //   let stateIndex = -1;
    
  //   for (let i = 0; i < spaceParts.length; i++) {
  //     if (statePatterns.test(spaceParts[i])) {
  //       stateIndex = i;
  //       break; // Stop at first state found
  //     }
  //   }
    
  //   // Extract suburb - usually the word(s) before state/postcode
  //   if (stateIndex > 0) {
  //     // Get words that are likely the suburb (before state)
  //     const suburbParts = [];
  //     for (let i = stateIndex - 1; i >= 0; i--) {
  //       const part = spaceParts[i];
  //       // Stop if we hit something that looks like a street number or name
  //       if (/^\d+$/.test(part) || /^(st|street|rd|road|ave|avenue|dr|drive|ln|lane|cres|crescent)$/i.test(part)) {
  //         break;
  //       }
  //       suburbParts.unshift(part);
  //     }
      
  //     if (suburbParts.length > 0) {
  //       return suburbParts.join(' ');
  //     }
  //   }
  // }
  
  // If all parsing fails, return the original address
  return suburb !== null ? suburb : fullAddress;
};

/**
 * Gets the next Monday from today and formats it
 * @param format - Optional format: 'short' returns "Mon 10 Feb", default returns "10 Feb 2026"
 * @returns The next Monday date in the specified format
 */
export const getNextMonday = (format: 'short' | 'default' = 'default'): string => {
  const today = new Date();
  const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  
  // Calculate days until next Monday
  // If today is Sunday (0), next Monday is 1 day away
  // If today is Monday (1), return today (0 days away)
  // If today is Tuesday (2), next Monday is 6 days away, etc.
  const daysUntilMonday = currentDay === 0 ? 1 : currentDay === 1 ? 0 : 8 - currentDay;
  
  const nextMonday = new Date(today);
  nextMonday.setDate(today.getDate() + daysUntilMonday);
  
  // Format the date
  const day = nextMonday.getDate();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[nextMonday.getMonth()];
  const year = nextMonday.getFullYear();
  
  // Return short format if requested
  if (format === 'short') {
    const weekday = nextMonday.toLocaleDateString('en-US', { weekday: 'short' });
    return `${weekday} ${day} ${month}`;
  }
  
  return `${day} ${month} ${year}`;
};
